import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './BrokerAuthBanner.module.css';

/** Global top-strip banner that appears whenever ANY AlpacaService call
 * returns a broker_auth_expired flag. Debounced at the emitter, dismissable,
 * and auto-hides when the user navigates to the broker settings tab. */
const BrokerAuthBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ hint?: string }>;
      setHint(ce.detail?.hint ?? null);
      setDismissed(false);
      setVisible(true);
    };
    window.addEventListener('signaldeck:broker-auth-expired', handler as EventListener);
    return () => window.removeEventListener('signaldeck:broker-auth-expired', handler as EventListener);
  }, []);

  // Auto-hide when the user lands on the broker settings — they've seen it.
  useEffect(() => {
    if (location.pathname === '/settings' && location.search.includes('broker')) {
      setVisible(false);
    }
  }, [location]);

  if (!visible || dismissed) return null;

  return (
    <div className={styles.banner} role="alert">
      <span className={styles.icon} aria-hidden>
        ⚠
      </span>
      <span className={styles.message}>
        <strong>Broker connection expired.</strong> {hint || 'Reconnect to restore access to positions and trading.'}
      </span>
      <button className={styles.cta} onClick={() => navigate('/settings?tab=broker')}>
        Reconnect
      </button>
      <button className={styles.dismiss} onClick={() => setDismissed(true)} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
};

export default BrokerAuthBanner;
